/* eslint-disable max-len */
/* eslint-disable react/jsx-props-no-spreading */
import React, { useState, useEffect } from 'react';
import { Box } from '@mui/material';
import { createTheme, ThemeProvider } from '@mui/material/styles';
import { makeStyles } from '@mui/styles';
import PropTypes from 'prop-types';
import { useQuery } from '@apollo/client';
import * as Q from '../../../../../constants/graphqlQueries';
import CustomDropdown from '../../../../components/Dropdown/CustomDropdown';
import CustomButtons from '../../../../components/Button/CustomButtons';
import colors from '../../../../../constants/colors';

const theme = createTheme({
  components: {
    MuiOutlinedInput: {
      styleOverrides: {
        root: {
          '& .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D5D5D5',
          },
          '&:hover .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D5D5D5',
          },
          '&.Mui-focused .MuiOutlinedInput-notchedOutline': {
            borderColor: '#D5D5D5',
          },
        },
      },
    },
  },
});

const useStyles = makeStyles(() => ({
  root: {
    margin: '0 14px',
    paddingBottom: '4px',
  },
  title: {
    fontWeight: '600',
    display: 'flex',
    alignItems: 'center',
    color: colors.blue,
    marginTop: '8px',
    marginBottom: '4px',
    fontSize: '1.6rem',
    borderBottom: '1px solid rgba(0,0,0,0.08)',
    paddingBottom: '6px',
  },
  rowFooter: {
    display: 'flex',
    justifyContent: 'center',
    gap: '8px',
    marginBottom: '4px',
    marginTop: '8px',
  }
}));
const filterLabels = {
  ACD: 'ACD',
  schedule: 'Schedule'
};
const $ALL = 'All';

const Search = ({ onSearchClick, filter, onClearClick, refetchSearch }) => {
  const classes = useStyles('');

  const [selectedCodeLabel, setSelectedCodeLabel] = useState();
  const [selectedACDState, setSelectedACDState] = useState();
  const [disabledACDState, setDisabledACDState] = useState(false);
  const [selectedScheduleType, setSelectedScheduleType] = useState();
  const [disabledScheduleType, setDisabledScheduleType] = useState(false);
  const [disabledButton, setDisabledButton] = useState(true);
  
  const [codeLabels, setCodeLabels] = useState({});
  const [ACDStateLabels, setACDStateLabels] = useState({});
  const [scheduleTypeLabels, setScheduleTypeLabels] = useState({});

  const { data: dataCodeMappingLabels, refetch: refetchCodeMAppingLabels } = useQuery(Q.CODE_MAPPING_CODE_LABELS_QUERY);
  const { data: dataACDState } = useQuery(Q.PERFORMANCE_ACD_STATUS);
  const { data: dataScheduleTypeLabels } = useQuery(Q.CODE_MAPPING_SCHEDULE_TYPE_QUERY);

  const handleCodeLabel = (value) => {
    if (value) {
      setSelectedCodeLabel(value);
      setDisabledButton(false);
    } else {
      setSelectedCodeLabel(value);
      if (!selectedACDState && !selectedScheduleType) setDisabledButton(true);
    };  
  };
  const handleACDState = (value) => {
    setSelectedACDState(value);
    if (!value && !selectedCodeLabel) setDisabledButton(true);
    else setDisabledButton(false);
  };
  const handleScheduleType = (value) => {
    if (value) {
    setSelectedScheduleType(value);
    setDisabledButton(false);
    } else {
      setSelectedScheduleType(value);
      if (!selectedCodeLabel) setDisabledButton(true);
    };
  };
  const handleClearClick = () => {
    setSelectedCodeLabel(null);
    setSelectedACDState(null);
    setSelectedScheduleType(null);
    setDisabledButton(true);
    onClearClick();
  };
  const handleSearchClick = () => {
    const searchTerms = {
      codeLabel: selectedCodeLabel?.CodeLabel,
      ACDState: selectedACDState?.ACDStateLabel,
      scheduleType: selectedScheduleType,
    };
    onSearchClick(searchTerms);
  };

  useEffect(() => {
    if (dataCodeMappingLabels && filter) {
      const filteredCodeLabels = dataCodeMappingLabels.codeMappingCodeLabels.filter((cm) => cm.CodeMappingType === filter);
      const codeLabelsOptions = [{ CodeLabel: $ALL, CodeMappingType: filter }, ...filteredCodeLabels] 
      const CodeLabelProps = { 
        options: codeLabelsOptions,
        getOptionLabel: (option) => option.CodeLabel
      }
      setCodeLabels(CodeLabelProps);
    };
    if (!filter) {
      setCodeLabels([]);
    };
    if (filter === filterLabels.schedule) {
      setSelectedACDState(null);
      setDisabledACDState(true);
      setDisabledScheduleType(false);
    };
    if (filter === filterLabels.ACD) {
      setSelectedScheduleType(null);
      setDisabledScheduleType(true);
      setDisabledACDState(false);
    };
  }, [dataCodeMappingLabels, filter]);
  useEffect(() => {
    if (dataACDState) {
      const ACDNameProps = { 
        options: dataACDState.GetPerformanceACDStateLabel,
        getOptionLabel: (option) => option.ACDStateLabel
      }
      setACDStateLabels(ACDNameProps);
    }
  }, [dataACDState]);
  useEffect(() => {
    if (dataScheduleTypeLabels) {
      // TODO: a hack to remove duplicated values
      const filteredScheduleTypes = [...new Set(dataScheduleTypeLabels.scheduleTypes.map((l) => l.Description))].sort();
      const scheduleTypesOptions = [$ALL, ...filteredScheduleTypes]
      const scheduleTypeProps = { 
        options: scheduleTypesOptions,
        getOptionLabel: (option) => option
      }
      setScheduleTypeLabels(scheduleTypeProps);
    }
  }, [dataScheduleTypeLabels]);
  useEffect(() => {
    if (refetchSearch) {
      refetchCodeMAppingLabels();
      setSelectedCodeLabel(null);
    };
  }, [refetchSearch]);

  return (
    <ThemeProvider theme={theme}>
      <Box className={classes.root}>
        <Box className={classes.title}>
          Search
        </Box>
        <CustomDropdown
          title='Code Label'
          value={selectedCodeLabel}
          handleChange={handleCodeLabel}
          defaultProps={codeLabels}
          mandatory
        />
        <CustomDropdown
          title='ACD State'
          value={selectedACDState}
          handleChange={handleACDState}
          defaultProps={ACDStateLabels}
          disabled={disabledACDState}
          mandatory={!disabledACDState}
        />
        <CustomDropdown
          title='Schedule Type'
          value={selectedScheduleType}
          handleChange={handleScheduleType}
          defaultProps={scheduleTypeLabels}
          disabled={disabledScheduleType}
          mandatory={!disabledScheduleType}
        />
        <Box className={classes.rowFooter}>
          <CustomButtons 
            handleClick={handleClearClick}
            name='Clear'
          />
          <CustomButtons 
            handleClick={handleSearchClick}
            name='Search'
            disabled={disabledButton}
            tooltipFree
          />
        </Box>
      </Box>
    </ThemeProvider>
  );
};

Search.propTypes = {
  onSearchClick: PropTypes.func.isRequired,
  onClearClick: PropTypes.func.isRequired,
  filter: PropTypes.string,
  refetchSearch: PropTypes.bool
};

Search.defaultProps = {
  filter: undefined,
  refetchSearch: false
};

export default Search;
